<template>
	<section :class="['hotel-infos', blockClassModifiers]">
		<div class="hotel-infos__infos-wrap">
			<HotelFacilitiesTeaser
				v-if="hotelInfo.Attributes && hotelInfo.Attributes?.length"
				:attributes="hotelInfo.Attributes"
				:top-seller="hotelInfo.Hotel.TopSeller"
				:rooms="hotelInfo.Hotel.RoomCount"
				@show-more-click="showMoreFacilities"
			/>
			<HotelGeneralDescription
				v-if="hotelInfo.Descriptions && hotelInfo.Descriptions?.length"
				:hotel-description="description"
				@show-more-button-click="showDescription"
			/>
		</div>
		<div class="hotel-infos__map-wrap">
			<div
				v-if="hasDescriptionOrAttributes"
				:class="OFFER_BOX_TELEPORT.HOTEL_INFOS"
			></div>
			<MapPreviewAndModal
				v-if="hotelRequestBody"
				id="hotel-info-map"
				class="hotel-infos__section-google-maps"
				:lat="hotelInfo.Location.Latitude"
				:lng="hotelInfo.Location.Longitude"
				:title="hotelInfo.Hotel.Name"
				:static-maps="mapData.staticMaps"
				:maps-key="mapData.key"
				:marker="false"
				:northwest
				:southeast
				:zoom="7"
				:show-note="false"
				:hotel-request-body="hotelRequestBody!"
				add-deep-links
				:hotel-to-show-iff-code="hotelInfo?.Hotel.IffCode"
				:hotel-to-show="hotelToShow"
			>
				<template #default="{ loadFullscreen }">
					<HotelMap
						:location="locationName"
						@click-hotel-map="() => (openMap(), loadFullscreen())"
					/>
				</template>
			</MapPreviewAndModal>
			<div
				v-if="!hasDescriptionOrAttributes"
				:class="OFFER_BOX_TELEPORT.HOTEL_INFOS"
			></div>
		</div>
	</section>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import MapPreviewAndModal from '@lmt-rpb/MapPreviewAndModal/MapPreviewAndModal.vue';
import HotelGeneralDescription from '@lmt-rpb/Molecules/HotelGeneralDescription/HotelGeneralDescription.vue';
import HotelFacilitiesTeaser from '@lmt-rpb/Molecules/HotelFacilitiesTeaser/HotelFacilitiesTeaser.vue';
import HotelMap from '@lmt-rpb/Molecules/HotelMap/HotelMap.vue';
import { OFFER_BOX_TELEPORT } from '@global-js/constants';
import { useStore } from '@/components/common/store';
import type { ItemType, OfferType } from '@/interfaces/hotel-list-types/hotel-list-types';
import type { OfferListTypoHotelData } from '@/interfaces/offer-list-types/offer-list-typo-hotel-data';
import type { StaticMaps } from '@/interfaces/google-maps';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import type { BaseOfferForHotelList, ApiDataTypes, ApiDataTypesKeys } from '@/components/common/types';

interface Props {
	hotelInfo: OfferListTypoHotelData;
	mapData: {
		key: string;
		staticMaps: StaticMaps;
	};
	bestOfferType?: OfferType | null;
}

const props = defineProps<Props>();

const store = useStore();

const searchMask = computed(() => store.state.searchMask);
const description = computed(() => props.hotelInfo.Descriptions);
const locationName = computed(() => props.hotelInfo.Location.Address.join('; '));
const hasDescriptionOrAttributes = computed(() => props.hotelInfo.Descriptions?.length || props.hotelInfo.Attributes?.length);

const blockClassModifiers = computed(() => ({
	'hotel-infos--no-infos': !hasDescriptionOrAttributes.value,
}));

const hotelToShow = computed<ItemType | null>((): BaseOfferForHotelList | null => {
	if (!props.hotelInfo || !props.bestOfferType) {
		return null;
	}
	const { Hotel, Location, NumberOfImages, RatingCount, PopularAttributes, Ratings } = props.hotelInfo;
	const offerKey: 'AccommodationOffer' | 'PackageOffer' =
		props.bestOfferType.TravelType === 'Package' ? 'PackageOffer' : 'AccommodationOffer';
	return {
		Hotel,
		Location: {
			...Location,
			City: {
				CityName: Location.City.Name,
				TtCyId: Location.City.TtCyid,
			},
			Region: {
				RegionName: Location.Region.Name,
				Climate: [],
				TtRId: Location.Region.TtRid,
			},
			Country: {
				...Location.Country,
				CountryName: Location.Country.Name,
				CountryCode: Location.Country.IsoCode,
			},
		},
		NumberOfImages,
		NumberOfRatings: RatingCount,
		PopularAttributes,
		Ratings,
		[offerKey]: {
			...props.bestOfferType,
			Board: props.bestOfferType?.BoardType,
		},
	};
});

const hotelRequestBody = ref<ApiDataTypes>();
const viewBoxOffset = 0.005;
const northwest = computed(() => ({
	lat: props.hotelInfo.Location.Latitude - viewBoxOffset,
	lng: props.hotelInfo.Location.Longitude - viewBoxOffset,
}));
const southeast = computed(() => ({
	lat: props.hotelInfo.Location.Latitude + viewBoxOffset,
	lng: props.hotelInfo.Location.Longitude + viewBoxOffset,
}));

const apiData = store.getters['searchMask/apiData'];
const showMoreFacilities = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_facilities', cType: 'ux_hotelpage_show_details' });
};
const showDescription = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_general_description', cType: 'ux_hotelpage_show_details' });
};
const openMap = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_map' });
};

function initDefaultSearchSettings() {
	const hotelOnly = store.state.searchMask.onlyHotel;
	const exclude: ApiDataTypesKeys[] = hotelOnly ? ['DepartureAirport', 'DirectFlight', 'Transfer'] : [];
	exclude.push('DefiniteStartDate', 'TourOperatorCodes', 'HotelIffCode', 'Cities', 'Regions');
	const travelType = hotelOnly ? 'Accommodation' : 'Package';
	hotelRequestBody.value = apiData({ travelType }, exclude);
}

onMounted(() => {
	initDefaultSearchSettings();
});

watch(searchMask, initDefaultSearchSettings, { deep: true });
</script>

<style lang="scss" scoped>
.hotel-infos {
	max-width: $breakpoint-container;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	align-items: start;
	gap: 2.4rem;

	&__infos-wrap {
		display: flex;
		flex-direction: column;
		align-items: start;
		gap: 2.4rem;

		&:empty {
			display: none;
		}
	}

	&__teleport-offer-box {
		min-width: 33rem;

		&:empty {
			display: none;
		}
	}

	&__map-wrap {
		display: flex;
		flex-direction: column;
		align-items: start;
		gap: 2.4rem;
	}

	&--no-infos {
		width: 100%;

		.hotel-infos__map-wrap {
			flex-direction: row;
			width: 100%;
			justify-content: space-between;
			max-width: 100%;
		}
	}

	@media (min-width: $breakpoint-small) {
		& {
			flex-direction: row;
			justify-content: space-between;
			align-items: start;
		}
	}

	@media (min-width: $breakpoint-large) {
		&__map-wrap {
			max-width: 60rem;
		}

		& {
			flex-direction: row;
		}
	}
}
</style>
